import { UserAccountDirectOrderListSection } from 'app/src/features/UserAccount/components/UserAccountDirectOrderListSection'
import { UserAccountDiscountOrderListSection } from 'app/src/features/UserAccount/components/UserAccountDiscountOrderListSection'
import { UserAccountMoneyTransferOrderListSection } from 'app/src/features/UserAccount/components/UserAccountMoneyTransferOrderListSection'
import { UserAccountOrderListFilterSection } from 'app/src/features/UserAccount/components/UserAccountOrderListFilterSection'
import { UserAccountRefundOrderListSection } from 'app/src/features/UserAccount/components/UserAccountRefundOrderListSection'
import { type OrderListFilters } from 'app/src/model'
import { catalogSummaryApi, membershipApi, type StatusConfigType } from 'app/src/services/welfareportal'
import type { CompanyWelfareConfiguration } from 'app/src/services/welfareportal/membership-api'
import { getFromSessionStorage } from 'app/src/shared/helpers'
import { useWebAppContext } from 'app/src/shared/hooks'
import { Button } from 'primereact/button'
import { Dropdown, type DropdownChangeEvent } from 'primereact/dropdown'
import { TabPanel, TabView } from 'primereact/tabview'
import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

export function UserAccountOrderListSection(props: {
  directStatusList: StatusConfigType[]
  refundStatusList: StatusConfigType[]
  moneyTransferStatusList: StatusConfigType[]
  discountStatusList: StatusConfigType[]
}): ReactElement {
  const { t } = useTranslation('userAccount', { keyPrefix: 'Tabs' })
  const { t: tFilterSection } = useTranslation('userAccount', { keyPrefix: 'FilterSection' })

  const context = useWebAppContext()

  // verifying visible catalogs to define tabs
  const catalogSummaryDetails = catalogSummaryApi.useCatalogSummaryList({
    pathParams: { templateBasePath: context.userInfo.welfareCampaign.configuration.frontend.templateId },
  })

  // taking stored campaign
  const campaignStorage = getFromSessionStorage('selectedCampaign') ?? '{}'

  // membershipId of campaign
  const [membershipId, setMembershipId] = useState(JSON.parse(campaignStorage)?.membershipId || '')

  // fetching selected campaign datas
  const membership = membershipApi.useMembershipGet({ pathParams: { membershipId: membershipId } })

  const [selectedUserMembershipToDisplay, setSelectedUserMembershipToDisplay] = useState<
    membershipApi.UserMembership | undefined
  >(context.userInfo.userMemberships.find((m) => m.membershipId === membershipId))

  // when campaign changes set correct filters
  useEffect(() => {
    setFilters((prev) => ({ ...prev, membershipId }))
  }, [membershipId])

  /** funnel utility function */
  const getFunnelDiscount = (config: CompanyWelfareConfiguration) => {
    const funnels = config?.funnels
    if (!funnels) {
      return { haveDiscount: false, onlyDiscount: false }
    }
    const haveDiscount = funnels.includes('DISCOUNT')
    return {
      haveDiscount,
      onlyDiscount: haveDiscount && funnels.length === 1,
    }
  }

  // callback funnel discount
  const funnelDiscount = useCallback(
    () => getFunnelDiscount(membership.data?.welfareCampaign.configuration as CompanyWelfareConfiguration),
    [membership.data],
  )()

  const defaultFilters: OrderListFilters = {
    sort: '-date',
    status: ['confirmed', 'used', 'submitted', 'refused', 'approved', 'canceled'],
    offset: 0,
    limit: context.portalConfiguration.paginatorConfig.initialPageSize,
    membershipId,
  }
  const defaultRefundFilters: OrderListFilters = {
    sort: '-date',
    status: ['SUBMITTED', 'IN_PROGRESS', 'REJECTED', 'CANCELED', 'REFUNDED'],
    offset: 0,
    limit: context.portalConfiguration.paginatorConfig.initialPageSize,
    membershipId,
  }
  const defaultTransferFilters: OrderListFilters = {
    sort: '-date',
    status: ['SUBMITTED', 'CONFIRMED', 'REJECTED', 'CANCELED'],
    offset: 0,
    limit: context.portalConfiguration.paginatorConfig.initialPageSize,
    membershipId,
  }

  const defaultDiscountFilters: OrderListFilters = {
    sort: '-date',
    status: ['confirmed', 'used', 'submitted', 'refused', 'approved', 'canceled'],
    offset: 0,
    limit: context.portalConfiguration.paginatorConfig.initialPageSize,
    membershipId,
  }

  interface AvailableCategory {
    type: string
    name: string
    defaultFilters: OrderListFilters
    statusList: StatusConfigType[]
  }

  const availableCategories: AvailableCategory[] = []

  // override Funzione di visibilità del catalogo con membership attuale
  const catalogVisibilityFunc = (
    catalogSummary: catalogSummaryApi.CatalogSummaryItem,
    configuration: CompanyWelfareConfiguration,
    applyHideCard: boolean,
  ) => {
    const funnel =
      configuration?.backofficeFunnels && configuration?.backofficeFunnels.length > 0
        ? configuration?.backofficeFunnels
        : configuration?.funnels
    return (
      (!applyHideCard || typeof catalogSummary?.hideCard === 'undefined' || !catalogSummary?.hideCard) &&
      (funnel?.some((y) => y === catalogSummary?.id) ?? false)
    )
  }

  // override che prende la catalogVisibilityFunc con membership attuale
  const hasCategory = (category: string) =>
    catalogSummaryDetails.data?.categories.find(
      (el) =>
        el.id?.trim().toUpperCase() == category &&
        catalogVisibilityFunc(el, membership.data?.welfareCampaign?.configuration as CompanyWelfareConfiguration, false),
    )

  const hasDirectCategory = hasCategory('DIRECT')

  if (hasDirectCategory) {
    availableCategories.push({
      type: 'DIRECT',
      name: t('Direct'),
      defaultFilters: defaultFilters,
      statusList: props.directStatusList,
    })
  }

  const hasRefundCategory = hasCategory('REFUND')
  if (hasRefundCategory) {
    availableCategories.push({
      type: 'REFUND',
      name: t('Refund'),
      defaultFilters: defaultRefundFilters,
      statusList: props.refundStatusList,
    })
  }

  const hasTransferCategory = hasCategory('TRANSFER')
  if (hasTransferCategory) {
    availableCategories.push({
      type: 'TRANSFER',
      name: t('MoneyTransfer'),
      defaultFilters: defaultTransferFilters,
      statusList: props.moneyTransferStatusList,
    })
  }

  if (funnelDiscount.haveDiscount) {
    availableCategories.push({
      type: 'DISCOUNT',
      name: t('Discount'),
      defaultFilters: defaultDiscountFilters,
      statusList: props.discountStatusList,
    })
  }

  // Build orderKinds after availableCategories is populated
  const orderKinds = availableCategories.map((category, index) => ({
    name: category.name,
    type: category.type,
    index: index,
    label: `Seleziona ${category.name}`,
    ariaLabel: `Seleziona ${category.name}`,
  }))

  // Always start with first tab active
  const [activeIndex, setActiveIndex] = useState(0)
  const [filters, setFilters] = useState<OrderListFilters>({
    ...(availableCategories[0]?.defaultFilters || defaultFilters),
    membershipId,
  })

  // filter button state
  const [visible, setVisible] = useState(false)

  // callback function for changing state
  const toggleVisibility = () => setVisible(!visible)

  useEffect(() => {
    setFilters((prev) => {
      if (availableCategories.length > 0) {
        return {
          ...availableCategories[0].defaultFilters,
          membershipId,
        }
      }
      // If categories are not ready yet, keep previous filters or fallback to defaults
      if (prev?.status && prev.status.length > 0) {
        return { ...prev, membershipId }
      }
      return {
        ...defaultFilters,
        membershipId,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCategories?.length])

  function changeIndex(idx: number) {
    setActiveIndex(idx)
    setVisible(false)
    if (availableCategories[idx]) {
      setFilters({
        ...availableCategories[idx].defaultFilters,
        membershipId,
      })
    }
  }

  function getTabDirect() {
    return (
      hasDirectCategory && (
        <TabPanel header={t('Direct')} key={t('Direct')} pt={{ content: { className: 'w-full min-w-0' } }}>
          <FilterButton />
          <UserAccountOrderListFilterSection
            visible={visible}
            statusList={props.directStatusList}
            selectedFilters={filters}
            defaultFilters={defaultFilters}
            updateFilters={setFilters}
          ></UserAccountOrderListFilterSection>
          <UserAccountDirectOrderListSection
            filtersToSend={filters}
            updateFilters={setFilters}
            directStatusList={props.directStatusList}
          />
        </TabPanel>
      )
    )
  }

  function getTabRefund() {
    return (
      hasRefundCategory && (
        <TabPanel header={t('Refund')} key={t('Refund')} pt={{ content: { className: 'w-full min-w-0' } }}>
          <FilterButton />
          <UserAccountOrderListFilterSection
            visible={visible}
            statusList={props.refundStatusList}
            defaultFilters={defaultRefundFilters}
            selectedFilters={filters}
            updateFilters={setFilters}
          ></UserAccountOrderListFilterSection>
          <UserAccountRefundOrderListSection
            filtersToSend={filters}
            updateFilters={setFilters}
            refundStatusList={props.refundStatusList}
          />
        </TabPanel>
      )
    )
  }

  function getTabTransfer() {
    return (
      hasTransferCategory && (
        <TabPanel header={t('MoneyTransfer')} key={t('MoneyTransfer')} pt={{ content: { className: 'w-full min-w-0' } }}>
          <FilterButton />
          <UserAccountOrderListFilterSection
            visible={visible}
            statusList={props.moneyTransferStatusList}
            selectedFilters={filters}
            defaultFilters={defaultTransferFilters}
            updateFilters={setFilters}
          ></UserAccountOrderListFilterSection>
          <UserAccountMoneyTransferOrderListSection
            filtersToSend={filters}
            updateFilters={setFilters}
            moneyTransferStatusList={props.moneyTransferStatusList}
          />
        </TabPanel>
      )
    )
  }

  function getTabDiscount() {
    return (
      funnelDiscount.haveDiscount && (
        <TabPanel header={t('Discount')} key={t('Discount')} pt={{ content: { className: 'w-full min-w-0' } }}>
          <FilterButton />
          <UserAccountOrderListFilterSection
            visible={visible}
            statusList={props.discountStatusList}
            selectedFilters={filters}
            defaultFilters={defaultDiscountFilters}
            updateFilters={setFilters}
          ></UserAccountOrderListFilterSection>
          <UserAccountDiscountOrderListSection
            filtersToSend={filters}
            updateFilters={setFilters}
            discountStatusList={props.discountStatusList}
          />
        </TabPanel>
      )
    )
  }

  /** Function changing campaign selected */
  const onCampaignChange = (e: DropdownChangeEvent) => {
    // set the inner dropdown field
    setSelectedUserMembershipToDisplay(e.value)
    // set the membership
    setMembershipId(e.value?.membershipId)
    // reset active tab to index 0
    setActiveIndex(0)
    // reset visibility to false
    setVisible(false)
  }

  /** Filter button component aligned in tab selections */
  const FilterButton = () => (
    <Button
      icon='pi pi-sliders-h'
      rounded
      onClick={toggleVisibility}
      aria-label={tFilterSection('FilterBtnLabel')}
      className='absolute right-5 top-1 hidden md:block'
      style={{ zIndex: 1 }}
    />
  )

  if (!catalogSummaryDetails.isError && !catalogSummaryDetails.isLoading)
    return (
      <>
        <div>
          <label htmlFor='campaignName' className='mr-1 block'>
            {tFilterSection('CampaignFilterLabel')}
          </label>
          <Dropdown
            aria-label='campaignName'
            inputId='campaignName'
            value={selectedUserMembershipToDisplay}
            onChange={onCampaignChange}
            itemTemplate={(option: membershipApi.UserMembership) => (
              <div className='flex items-center'>
                <i
                  className={`pi pi-circle-fill mr-2 ${option.active ? 'text-green-400' : 'text-gray-400'}`}
                  style={{ fontSize: '0.75rem' }}
                ></i>
                <span className='ml-2'>{option.campaignName}</span>
              </div>
            )}
            options={context.userInfo.userMemberships}
            optionLabel='campaignName'
            placeholder={tFilterSection('CampaignFilterDropdownPlaceholder')}
            className='mb-4 w-full md:w-auto'
          />
        </div>
        <div className='relative hidden md:block'>
          <TabView scrollable activeIndex={activeIndex} onTabChange={(e) => changeIndex(e.index)}>
            {availableCategories.map((category) => {
              switch (category.type) {
                case 'DIRECT':
                  return getTabDirect()
                case 'REFUND':
                  return getTabRefund()
                case 'TRANSFER':
                  return getTabTransfer()
                case 'DISCOUNT':
                  return getTabDiscount()
              }
            })}
          </TabView>
        </div>

        <div className='block md:hidden'>
          <label htmlFor='order-type-dropdown' className='mr-1 block'>
            {tFilterSection('TabsLabel')}
          </label>
          <Dropdown
            aria-label='order-type-dropdown'
            inputId='order-type-dropdown'
            className='w-full'
            value={orderKinds[activeIndex]}
            onChange={(e) => {
              changeIndex(e.value.index)
            }}
            options={orderKinds}
            optionLabel='name'
            placeholder='Seleziona tipo di ordine'
            pt={{ trigger: { 'aria-label': 'Seleziona tipo di ordine' } }}
          />
          <div className='my-4 flex items-center justify-end gap-2'>
            <div>{tFilterSection('FilterBtnLabel')}</div>
            <Button icon='pi pi-sliders-h' rounded onClick={toggleVisibility} aria-label={tFilterSection('FilterBtnLabel')} />
          </div>

          <TabView
            data-testid='UserAccountOrderListSectionMobileTabView'
            activeIndex={activeIndex}
            onTabChange={(e) => changeIndex(e.index)}
            pt={{ panelContainer: { className: 'w-full min-w-0 !p-0' } }}
          >
            {availableCategories.map((category) => {
              switch (category.type) {
                case 'DIRECT':
                  return getTabDirect()
                case 'REFUND':
                  return getTabRefund()
                case 'TRANSFER':
                  return getTabTransfer()
                case 'DISCOUNT':
                  return getTabDiscount()
              }
            })}
          </TabView>
        </div>
      </>
    )
  else return <div />
}
